/**
 * Riders Corner Card Component
 *
 * Single Responsibility: Displays a single riders corner trip card
 * Open/Closed: Can be extended with additional props without modifying existing code
 */

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { typographyColors, spacing } from '@/design-system/utils';
import { dimensions } from '@/design-system/tokens/dimensions';
import { RidersCornerListItem } from '@/services/ridersCornerService';

interface RidersCornerCardProps {
  item: RidersCornerListItem;
  className?: string;
}

export function RidersCornerCard({
  item,
  className = '',
}: RidersCornerCardProps) {
  const {
    _id,
    slug,
    title,
    description,
    shortDescription,
    category,
    imageUrl,
    totalDistance,
    totalHoursFrom,
    totalHoursTo,
  } = item;

  const displayImage = imageUrl?.[0] || '/assets/images/No-Image.jpg';
  const displayCategory = category?.title || 'Ride';
  const distance = `${totalDistance} kms`;
  const duration = `${totalHoursFrom} - ${totalHoursTo} hrs`;

  // Use shortDescription for listing page, fallback to description
  const displayDescription = shortDescription || description || '';

  // Use slug for navigation to match the detail page route
  const linkSlug = slug || _id;

  return (
    <Link href={`/riders-corner/${linkSlug}`}>
      <div
        className={`flex flex-col md:flex-row bg-white cursor-pointer hover:opacity-90 transition-opacity flex-shrink-0 ${className}`}
        style={{
          borderRadius: '12px',
          border: '1px solid #e7e5e4',
          boxShadow: '0px 16px 30px rgba(25, 24, 19, 0.06)',
          padding: '12px 16px 12px 12px',
          width: '100%',
          gap: '16px',
          alignSelf: 'start',
          overflow: 'hidden',
          alignItems: 'stretch', // Ensure children stretch to full height
        }}
      >
        {/* Image with Category Badge Overlay */}
        <div
          className='relative w-full md:w-[213px] rounded-[8px] overflow-hidden flex-shrink-0 md:self-stretch'
          style={{
            height: dimensions.component.cardRide.imageHeight, // 220px on mobile
            minHeight: dimensions.component.cardRide.imageHeight,
          }}
        >
          <Image
            src={displayImage}
            alt={title}
            fill
            className='object-cover'
            sizes='(max-width: 768px) 100vw, 213px'
            loading='lazy'
          />
          {/* Category Badge - Overlaid on top right of image */}
          <div
            className='absolute top-2 right-2 z-10'
            style={{
              pointerEvents: 'none',
            }}
          >
            <span
              className='inline-flex items-center justify-center font-medium text-sm rounded-[4px]'
              style={{
                backgroundColor: '#f6ebb5',
                border: '1px solid #fde047',
                color: typographyColors.primary,
                padding: '2px 8px',
                gap: '2px',
              }}
            >
              {displayCategory}
            </span>
          </div>
        </div>

        {/* Content */}
        <div
          className='flex-1 flex flex-col justify-between'
          style={{
            minHeight: 0,
            overflow: 'hidden',
            paddingTop: spacing[4], // 16px
            paddingBottom: spacing[4], // 16px for bottom padding
            gap: spacing[6], // 24px
          }}
        >
          <div
            style={{
              flexShrink: 0,
              gap: spacing[4],
              paddingTop: spacing[1],
              paddingBottom: spacing[1],
            }}
            className='flex flex-col'
          >
            {/* Title and Description */}
            <div style={{ gap: '6px' }} className='flex flex-col'>
              <h3
                className='text-2xl font-bold'
                style={{
                  color: typographyColors.primary,
                  margin: 0,
                  lineHeight: '1.2',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxHeight: 'calc(1.2em * 2)', // Ensure maximum 2 lines height
                }}
              >
                {title}
              </h3>
              <p
                className='text-base'
                style={{
                  color: typographyColors.secondary,
                  margin: 0,
                  fontWeight: 300,
                  lineHeight: '1.5',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minHeight: 'calc(1.5em * 2)', // Ensure minimum 2 lines height
                  maxHeight: 'calc(1.5em * 2)', // Ensure maximum 2 lines height
                }}
              >
                {displayDescription}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div
            className='flex items-center flex-wrap'
            style={{ flexShrink: 0, gap: spacing[2] }}
          >
            <Pill
              iconSrc='/assets/images/icons/motorcycle_vector.svg'
              label={distance}
              isClock={false}
            />
            <Pill
              iconSrc='/assets/images/icons/clock-outline.svg'
              label={duration}
              isClock={true}
            />
          </div>
        </div>
      </div>
    </Link>
  );
}

interface PillProps {
  iconSrc: string;
  label: string;
  isClock?: boolean;
}

function Pill({ iconSrc, label, isClock = false }: PillProps) {
  const commonPillStyle = {
    display: 'flex',
    padding: '4px 8px 4px 4px',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#f5f5f4',
    border: '1px solid #e7e5e4',
    borderRadius: '6px',
    flexShrink: 0,
  };

  if (isClock) {
    return (
      <div style={commonPillStyle} className='flex'>
        <div
          className='flex flex-col items-center justify-center'
          style={{
            backgroundColor: '#e7e5e4',
            borderRadius: '2px',
            padding: '3px 4px',
            gap: '8px',
            flexShrink: 0,
            width: '26.333px',
            height: '19.333px',
            minWidth: '26.333px',
          }}
        >
          <div
            className='relative'
            style={{
              width: '16px',
              height: '16px',
            }}
          >
            <Image src={iconSrc} alt={label} width={16} height={16} />
          </div>
        </div>
        <span
          className='text-sm font-medium'
          style={{ color: '#44403c', lineHeight: 'normal' }}
        >
          {label}
        </span>
      </div>
    );
  }

  // Motorcycle pill
  return (
    <div style={commonPillStyle} className='flex flex-row'>
      <div
        className='relative flex-shrink-0'
        style={{
          width: '26.333px',
          height: '19.333px',
        }}
      >
        <div
          className='absolute inset-0 flex items-center justify-center'
          style={{
            backgroundColor: '#e7e5e4',
            borderRadius: '2px',
          }}
        >
          <Image src={iconSrc} alt={label} width={16} height={16} />
        </div>
      </div>
      <span
        className='text-sm font-medium text-right'
        style={{ color: '#44403c', lineHeight: 'normal' }}
      >
        {label}
      </span>
    </div>
  );
}
